// gameClock.ts — Shared simulation clock. The render loop runs on wall-clock time,
// but the *game* advances at a user-chosen multiplier (1×–1000×). One frame
// component (<GameClock>) integrates delta*speed into `time` each frame; every
// orbital body and the HUD calendar read `gameClock.time` (game-seconds) instead of
// the raw render clock, so changing speed re-times the whole simulation at once
// without re-rendering React every frame. A plain mutable singleton — not React
// state — precisely because it changes 60×/s and must not trigger renders.
export const gameClock = { time: 0, speed: 1 };

// Speed presets surfaced in the HUD. 1× is real-time (one game-second per
// wall-second); higher multipliers fast-forward the simulation + calendar.
export const SPEED_STEPS = [1, 1000, 10000, 1000000] as const;

// clockAnchor ties the local clock to SERVER time. The simulation is now
// server-authoritative: every /api/state snapshot stamps the server's gameTime +
// speed here, and <GameClock> extrapolates `gameClock.time` from it between snapshots
// (so coarse 4 Hz snapshots still render smoothly). `live` is false until the first
// snapshot — and stays false offline (no backend) — where the clock falls back to
// local integration so dev-without-a-server still ticks.
export const clockAnchor = { time: 0, speed: 1, at: 0, live: false };

function nowSeconds(): number {
  return (typeof performance !== 'undefined' ? performance.now() : 0) / 1000;
}

/** Re-anchor the local clock to an authoritative server sample. */
export function anchorClock(serverTime: number, speed: number): void {
  clockAnchor.time = serverTime;
  clockAnchor.speed = speed;
  clockAnchor.at = nowSeconds();
  clockAnchor.live = true;
}

/** Current extrapolated game-time from the live anchor (server-driven). */
export function anchoredTime(): number {
  return clockAnchor.time + clockAnchor.speed * (nowSeconds() - clockAnchor.at);
}
